import { useMemo, useState } from 'react';
import { T, useGT, Var } from 'gt-react';
import { TrashIcon } from '@phosphor-icons/react';
import ConfirmDialog from '@/components/common/ConfirmDialog';
import { Combobox } from '@/components/ui/combobox';
import { pickerLabels } from '@/components/common/inputs/pickerLabels';
import {
  FilterPanelContent,
  type FilterGroup,
} from '@/components/rebates/FilterPanelContent';
import type { MultiSelectOption } from '@/components/common/MultiSelect';
import { useToast } from '@/context/ToastContext';
import type {
  PurchaseSupplierOption,
  PurchaseUploadOption,
} from './purchaseTrendPage';

export function PurchaseTrendFilters({
  uploadOptions,
  selectedUploadId,
  onUploadChange,
  supplierOptions,
  selectedSuppliers,
  onSuppliersChange,
  divisionOptions,
  selectedDivisions,
  onDivisionsChange,
  canRemoveUploads = false,
  onRemoveUpload,
  removeUploadLoading = false,
}: {
  uploadOptions: PurchaseUploadOption[];
  selectedUploadId: string | null;
  onUploadChange: (uploadId: string | null) => void;
  supplierOptions: PurchaseSupplierOption[];
  selectedSuppliers: MultiSelectOption[];
  onSuppliersChange: (suppliers: MultiSelectOption[]) => void;
  divisionOptions: MultiSelectOption[];
  selectedDivisions: MultiSelectOption[];
  onDivisionsChange: (divisions: MultiSelectOption[]) => void;
  canRemoveUploads?: boolean;
  onRemoveUpload?: (uploadId: string) => Promise<void>;
  removeUploadLoading?: boolean;
}) {
  const gt = useGT();
  const { showToast } = useToast();
  const [pendingRemove, setPendingRemove] =
    useState<PurchaseUploadOption | null>(null);
  const [fileOpen, setFileOpen] = useState(false);
  const fileLabel = gt('File', {
    context: 'filter label for purchases upload',
  });
  const filePlaceholder = gt('Select file', {
    context: 'placeholder for purchases upload picker',
  });
  const filterGroups: FilterGroup[] = useMemo(
    () => [
      {
        label: gt('Supplier', {
          context: 'filter label for rebate supplier',
        }),
        options: supplierOptions,
        value: selectedSuppliers,
        onChange: onSuppliersChange,
        placeholder: gt('All rebate suppliers', {
          context: 'placeholder when no supplier is selected',
        }),
        searchPlaceholder: gt('Search suppliers...', {
          context: 'search placeholder for supplier picker',
        }),
      },
      {
        label: gt('Divisions', {
          context: 'filter label for divisions',
        }),
        options: divisionOptions,
        value: selectedDivisions,
        onChange: onDivisionsChange,
        placeholder: gt('All divisions', {
          context: 'placeholder when no division is selected',
        }),
        searchPlaceholder: gt('Search divisions...', {
          context: 'search placeholder for division picker',
        }),
      },
    ],
    [
      divisionOptions,
      gt,
      onDivisionsChange,
      onSuppliersChange,
      selectedDivisions,
      selectedSuppliers,
      supplierOptions,
    ],
  );
  return (
    <div
      className="flex flex-wrap items-end gap-4"
      data-testid="purchase-trend-filters"
    >
      <div className="flex flex-col min-w-[180px] max-w-[180px]">
        <label
          htmlFor="purchase-trend-file"
          className="text-sm font-medium mb-1.5"
        >
          {fileLabel}
        </label>
        <div className="flex w-full min-w-0">
          <Combobox
            id="purchase-trend-file"
            items={uploadOptions}
            getValue={(option) => option.id}
            getLabel={(option) => option.label}
            value={selectedUploadId}
            open={fileOpen}
            onOpenChange={setFileOpen}
            onValueChange={(_id, item) => {
              onUploadChange(item?.value ?? selectedUploadId);
            }}
            allowClear={false}
            highlightFirst={false}
            closeButton
            modal={false}
            className="h-8 justify-between font-normal"
            contentStyle={{
              minWidth: '300px',
              maxWidth: 'min(80vw, 800px)',
            }}
            labels={pickerLabels({
              placeholder: filePlaceholder,
              search: 'Search',
            })}
            renderItem={
              canRemoveUploads
                ? (option) => (
                    <span className="flex w-full min-w-0 items-center justify-between gap-2">
                      <span className="min-w-0 truncate">{option.label}</span>
                      <button
                        type="button"
                        data-testid={`remove-purchase-upload-${option.id}`}
                        aria-label={gt('Remove {fileName}', {
                          context:
                            'remove a purchases upload from the file picker',
                          fileName: option.fileName,
                        })}
                        className="text-muted-foreground hover:text-destructive shrink-0 rounded-sm p-1"
                        onPointerDown={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                          setFileOpen(false);
                          window.setTimeout(() => {
                            setPendingRemove(option);
                          }, 0);
                        }}
                        onMouseDown={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                        }}
                        onClick={(event) => {
                          event.preventDefault();
                          event.stopPropagation();
                        }}
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </span>
                  )
                : undefined
            }
          />
        </div>
      </div>
      <FilterPanelContent layout="horizontal" filterGroups={filterGroups} />
      <ConfirmDialog
        open={pendingRemove !== null}
        onClose={() => setPendingRemove(null)}
        onConfirm={() => {
          if (!pendingRemove || !onRemoveUpload) {
            return;
          }
          void onRemoveUpload(pendingRemove.id)
            .then(() => {
              setPendingRemove(null);
            })
            .catch((error: unknown) => {
              showToast({
                title: gt('Could not remove purchase file', {
                  context:
                    'error title when a purchases upload fails to delete',
                }),
                message:
                  error instanceof Error
                    ? error.message
                    : gt('Unknown error occurred.', {
                        context:
                          'fallback error when a purchases upload fails to delete',
                      }),
                color: 'error',
              });
            });
        }}
        title={gt('Remove purchase file', {
          context: 'title for removing a purchases upload',
        })}
        confirmText={gt('Remove', {
          context: 'confirm removing a purchases upload',
        })}
        cancelText={gt('Cancel', {
          context: 'cancel removing a purchases upload',
        })}
        loading={removeUploadLoading}
        dangerous
        closeOnConfirm={false}
      >
        <p className="text-sm text-muted-foreground">
          <T context="confirm that a purchases upload and its stored rows will be deleted">
            Remove <Var>{pendingRemove?.fileName}</Var> from the upload history?
            This deletes the stored rows for this file.
          </T>
        </p>
      </ConfirmDialog>
    </div>
  );
}
